import { useEffect, useRef, useCallback, useState } from 'react';

/**
 * Accessibility Hook for Modal Components
 * 
 * Provides comprehensive accessibility features:
 * - Focus management and trapping
 * - Keyboard navigation
 * - Screen reader announcements
 * - ARIA live regions
 * - High contrast detection
 */

export const useModalAccessibility = (isOpen, modalId = 'modal') => {
  const modalRef = useRef(null);
  const previousFocusRef = useRef(null);
  const focusableElementsRef = useRef([]);
  const [announcements, setAnnouncements] = useState([]);
  const [isHighContrast, setIsHighContrast] = useState(false);
  const announcementIdRef = useRef(0);

  // Detect high contrast mode
  useEffect(() => {
    const checkHighContrast = () => {
      if (window.matchMedia) {
        const highContrastQuery = window.matchMedia('(prefers-contrast: high)');
        setIsHighContrast(highContrastQuery.matches);
        
        const handleChange = (e) => setIsHighContrast(e.matches);
        highContrastQuery.addEventListener('change', handleChange);
        
        return () => highContrastQuery.removeEventListener('change', handleChange);
      }
    };
    
    return checkHighContrast();
  }, []);

  // Get all focusable elements within the modal
  const getFocusableElements = useCallback(() => {
    if (!modalRef.current) return [];
    
    const focusableSelectors = [
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      'a[href]',
      '[tabindex]:not([tabindex="-1"])',
      '[role="button"]:not([disabled])',
      '[role="link"]',
      '[role="menuitem"]',
      '[role="option"]'
    ].join(', ');
    
    return Array.from(modalRef.current.querySelectorAll(focusableSelectors))
      .filter(el => {
        // Check if element is actually visible and focusable
        const style = window.getComputedStyle(el);
        return style.display !== 'none' && 
               style.visibility !== 'hidden' && 
               !el.hasAttribute('aria-hidden');
      });
  }, []);

  // Focus management
  const trapFocus = useCallback((e) => {
    if (!isOpen || !modalRef.current) return;
    
    const focusableElements = getFocusableElements();
    focusableElementsRef.current = focusableElements;
    
    if (focusableElements.length === 0) return;
    
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];
    
    if (e.key === 'Tab') {
      if (e.shiftKey) {
        // Shift + Tab
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      } else {
        // Tab
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    }
  }, [isOpen, getFocusableElements]);

  // Keyboard navigation handler
  const handleKeyDown = useCallback((e) => {
    if (!isOpen) return;
    
    switch (e.key) {
      case 'Escape':
        e.preventDefault();
        // Announce modal closing
        announce('Modal closed');
        // Let parent handle the actual closing
        if (modalRef.current) {
          const closeEvent = new CustomEvent('modal-close-requested', {
            bubbles: true,
            detail: { source: 'keyboard' }
          });
          modalRef.current.dispatchEvent(closeEvent);
        }
        break;
        
      case 'Tab':
        trapFocus(e);
        break;
        
      case 'ArrowDown':
      case 'ArrowUp':
        // Handle arrow key navigation for lists
        handleArrowNavigation(e);
        break;
        
      case 'Enter':
      case ' ':
        // Handle activation of focused elements
        handleActivation(e);
        break;
        
      case 'Home':
        e.preventDefault();
        focusFirst();
        break;
        
      case 'End':
        e.preventDefault();
        focusLast();
        break;
        
      default:
        // No action for other keys
        break;
    }
  }, [isOpen, trapFocus]);

  // Arrow key navigation for lists and grids
  const handleArrowNavigation = useCallback((e) => {
    const activeElement = document.activeElement;
    if (!activeElement) return;
    
    // Check if we're in a list or grid context
    const listContainer = activeElement.closest('[role="listbox"], [role="grid"], [role="menu"]');
    if (!listContainer) return;
    
    const items = Array.from(listContainer.querySelectorAll('[role="option"], [role="gridcell"], [role="menuitem"]'));
    const currentIndex = items.indexOf(activeElement);
    
    if (currentIndex === -1) return;
    
    let nextIndex;
    if (e.key === 'ArrowDown') {
      nextIndex = Math.min(currentIndex + 1, items.length - 1);
    } else if (e.key === 'ArrowUp') {
      nextIndex = Math.max(currentIndex - 1, 0);
    }
    
    if (nextIndex !== undefined && items[nextIndex]) {
      e.preventDefault();
      items[nextIndex].focus();
      
      // Announce the focused item
      const itemText = items[nextIndex].textContent || items[nextIndex].getAttribute('aria-label');
      if (itemText) {
        announce(`${itemText}, ${nextIndex + 1} of ${items.length}`);
      }
    }
  }, []);

  // Handle Enter/Space activation
  const handleActivation = useCallback((e) => {
    const activeElement = document.activeElement;
    if (!activeElement) return;
    
    // Only handle if it's not already a button or link
    if (activeElement.tagName === 'BUTTON' || activeElement.tagName === 'A') return;
    
    // Check for custom activation handlers
    if (activeElement.hasAttribute('data-activatable')) {
      e.preventDefault();
      activeElement.click();
    }
  }, []);

  // Focus management utilities
  const focusFirst = useCallback(() => {
    const focusableElements = getFocusableElements();
    if (focusableElements.length > 0) {
      focusableElements[0].focus();
    }
  }, [getFocusableElements]);

  const focusLast = useCallback(() => {
    const focusableElements = getFocusableElements();
    if (focusableElements.length > 0) {
      focusableElements[focusableElements.length - 1].focus();
    }
  }, [getFocusableElements]);

  // Screen reader announcements
  const announce = useCallback((message, priority = 'polite') => {
    if (!message) return;
    
    // Generate unique ID using counter + timestamp + modalId
    announcementIdRef.current += 1;
    const uniqueId = `${modalId}_${announcementIdRef.current}_${Date.now()}`;
    
    const announcement = {
      id: uniqueId,
      message,
      priority,
      timestamp: new Date().toISOString()
    };
    
    setAnnouncements(prev => [...prev, announcement]);
    
    // Clean up old announcements
    setTimeout(() => {
      setAnnouncements(prev => prev.filter(a => a.id !== announcement.id));
    }, 5000);
  }, [modalId]);

  // Modal lifecycle management
  useEffect(() => {
    if (isOpen) {
      // Store previous focus
      previousFocusRef.current = document.activeElement;
      
      // Add event listeners
      document.addEventListener('keydown', handleKeyDown);
      
      // Focus the modal after a brief delay to ensure it's rendered
      setTimeout(() => {
        if (modalRef.current) {
          // Try to focus the first focusable element, or the modal itself
          const focusableElements = getFocusableElements();
          if (focusableElements.length > 0) {
            focusableElements[0].focus();
          } else {
            modalRef.current.focus();
          }
          
          // Announce modal opening
          announce(`${modalId} opened`);
        }
      }, 100);
      
    } else {
      // Remove event listeners
      document.removeEventListener('keydown', handleKeyDown);
      
      // Restore previous focus
      if (previousFocusRef.current && typeof previousFocusRef.current.focus === 'function') {
        try {
          previousFocusRef.current.focus();
        } catch (e) {
          // Fallback to body if previous element is no longer focusable
          document.body.focus();
        }
      }
    }
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, handleKeyDown, getFocusableElements, modalId, announce]);

  // Generate ARIA attributes for the modal
  const getModalProps = useCallback(() => ({
    ref: modalRef,
    role: 'dialog',
    'aria-modal': 'true',
    'aria-labelledby': `${modalId}-title`,
    'aria-describedby': `${modalId}-description`,
    tabIndex: -1,
    'data-modal-id': modalId
  }), [modalId]);

  // Generate ARIA attributes for interactive elements
  const getInteractiveProps = useCallback((role = 'button', label = '', description = '') => ({
    role,
    'aria-label': label,
    'aria-describedby': description ? `${modalId}-${description}` : undefined,
    tabIndex: 0,
    'data-activatable': role !== 'button' && role !== 'link' ? 'true' : undefined
  }), [modalId]);

  // Generate ARIA attributes for lists
  const getListProps = useCallback((itemCount = 0) => ({
    role: 'listbox',
    'aria-label': `Results list, ${itemCount} items`,
    'aria-live': 'polite',
    'aria-atomic': 'false'
  }), []);

  const getListItemProps = useCallback((index, total, label = '') => ({
    role: 'option',
    'aria-label': `${label}, ${index + 1} of ${total}`,
    'aria-posinset': index + 1,
    'aria-setsize': total,
    tabIndex: 0
  }), []);

  return {
    // Refs and state
    modalRef,
    isHighContrast,
    announcements,
    
    // Focus management
    focusFirst,
    focusLast,
    getFocusableElements,
    
    // Announcements
    announce,
    
    // ARIA props generators
    getModalProps,
    getInteractiveProps,
    getListProps,
    getListItemProps,
    
    // Utilities
    trapFocus: handleKeyDown
  };
};

// Hook for managing loading state announcements
export const useLoadingAnnouncements = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { announce } = useModalAccessibility(true, 'loading');
  
  const announceLoading = useCallback((message = 'Loading') => {
    setIsLoading(true);
    announce(message, 'assertive');
  }, [announce]);
  
  const announceLoaded = useCallback((message = 'Content loaded') => {
    setIsLoading(false);
    announce(message, 'polite');
  }, [announce]);
  
  const announceError = useCallback((message = 'Error loading content') => {
    setIsLoading(false);
    announce(message, 'assertive');
  }, [announce]);
  
  return {
    isLoading,
    announceLoading,
    announceLoaded,
    announceError
  };
};

// Hook for managing focus within specific components
export const useFocusManagement = (containerRef, isActive = true) => {
  const [focusedIndex, setFocusedIndex] = useState(0);
  
  const focusableElements = useCallback(() => {
    if (!containerRef.current) return [];
    return Array.from(containerRef.current.querySelectorAll(
      'button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
    ));
  }, [containerRef]);
  
  const focusElement = useCallback((index) => {
    const elements = focusableElements();
    if (elements[index]) {
      elements[index].focus();
      setFocusedIndex(index);
    }
  }, [focusableElements]);
  
  const focusNext = useCallback(() => {
    const elements = focusableElements();
    const nextIndex = Math.min(focusedIndex + 1, elements.length - 1);
    focusElement(nextIndex);
  }, [focusedIndex, focusElement, focusableElements]);
  
  const focusPrevious = useCallback(() => {
    const nextIndex = Math.max(focusedIndex - 1, 0);
    focusElement(nextIndex);
  }, [focusedIndex, focusElement]);
  
  return {
    focusedIndex,
    focusElement,
    focusNext,
    focusPrevious,
    focusableElements: focusableElements()
  };
};
